import {
  runMoveCanvasWorkflow,
  runRenameCanvasWorkflow,
  runShareCanvasWorkflow,
} from "@/action-dialog";
import { restoreErrorMessage, useRestoreCanvas } from "@/api/archived";
import { useDeleteCanvas } from "@/api/canvas";
import { useToggleFavorite } from "@/api/resourceTags";
import { showMovedToTrashToast } from "@/components/ConfirmTrashDialog/movedToTrashToast";
import { ROUTES } from "@/constants/routes";
import type { ObjectActionHandlers } from "@/object-actions/types";
import { route } from "@/utils/route";
import { RESOURCE_TAG_FAVORITE, type Canvas } from "@cohesive/api-client";
import { useMemo } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";

export interface UseCanvasObjectActionHandlersParams {
  canvas: Canvas;
  canShare?: boolean;
  canDelete?: boolean;
}

export function useCanvasObjectActionHandlers({
  canvas,
  canShare = true,
  canDelete = true,
}: UseCanvasObjectActionHandlersParams): ObjectActionHandlers {
  const navigate = useNavigate();
  const toggleFavorite = useToggleFavorite();
  const deleteCanvas = useDeleteCanvas({
    onError: () => toast.error("Failed to delete canvas"),
  });
  const restoreCanvas = useRestoreCanvas({
    onError: (err) =>
      toast.error(restoreErrorMessage(err, "Failed to restore Canvas")),
  });

  const isFavorite = !!canvas.tags?.includes(RESOURCE_TAG_FAVORITE);

  return useMemo(
    (): ObjectActionHandlers => ({
      // Favoriting only needs view access to the canvas — it is independent of
      // share permission, so these stay available even when canShare is false.
      star: () =>
        toggleFavorite.mutate({
          resourceType: "canvas",
          resourceId: canvas.id,
          isFavorite,
        }),
      unstar: () =>
        toggleFavorite.mutate({
          resourceType: "canvas",
          resourceId: canvas.id,
          isFavorite,
        }),
      open: () =>
        navigate(route(ROUTES.CANVAS.DETAIL, { canvasId: canvas.id })),
      rename: () =>
        window.setTimeout(() => void runRenameCanvasWorkflow({ canvas }), 0),
      move: () =>
        window.setTimeout(() => void runMoveCanvasWorkflow({ canvas }), 0),
      share: canShare
        ? () =>
            window.setTimeout(() => void runShareCanvasWorkflow({ canvas }), 0)
        : undefined,
      delete: canDelete
        ? () =>
            deleteCanvas.mutate(canvas.id, {
              onSuccess: () =>
                showMovedToTrashToast(() => restoreCanvas.mutate(canvas.id)),
            })
        : undefined,
    }),
    [
      canvas,
      canDelete,
      canShare,
      deleteCanvas,
      isFavorite,
      navigate,
      restoreCanvas,
      toggleFavorite,
    ],
  );
}
